import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import rooms from '../assets/rooms.json';
import amenityIcons from '../assets/amenityIcons.json';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import '../styles/RoomDetail.css';
import ImageSlider from './ImageSlider';
import { Typography } from '@mui/material';

export default function RoomDetail() {
	// ALL hooks must be at the very top level, before ANY conditional logic
	const location = useLocation();
	const [lightboxOpen, setLightboxOpen] = useState(false);
	const [photoIndex, setPhotoIndex] = useState(0);
	const [isPreloading, setIsPreloading] = useState(false);

	// Derive values after hooks
	const roomName = location.pathname.split('/').pop();
	const room = rooms.find((r) => r.name === roomName);
	const images = useMemo(() => {
		return room ? [room.img, ...room.images] : [];
	}, [room?.img, room?.images]);

	// Preload images function
	const preloadImages = (startIndex = 0, count = 3) => {
		const imagesToPreload = [];

		for (let i = 0; i < count; i++) {
			const index = (startIndex + i) % images.length;
			imagesToPreload.push(images[index]);
		}

		return Promise.all(
			imagesToPreload.map((src) => {
				return new Promise((resolve) => {
					const img = new Image();
					img.onload = () => resolve(src);
					img.onerror = () => resolve(src); // Still resolve on error to not block
					img.src = src;

					// Timeout for slow connections (5 seconds)
					setTimeout(() => resolve(src), 5000);
				});
			})
		);
	};

	// Handle image click with preloading
	const handleImageClick = async (index) => {
		setPhotoIndex(index);
		setIsPreloading(true);

		try {
			// Preload current, next, and previous images
			await preloadImages(index, 3);
		} catch (error) {
			console.warn('Some images failed to preload:', error);
		} finally {
			setIsPreloading(false);
			setLightboxOpen(true);
		}
	};

	useEffect(() => {
		const setAmenitiesHeight = () => {
			const innerDiv = document.querySelector('.new-div-inner');
			const amenitiesWrapper = document.querySelector(
				'.amenities-tiles-wrapper'
			);

			amenitiesWrapper.style.height = '0px';

			if (innerDiv && amenitiesWrapper) {
				if (window.innerWidth < 768) {
					amenitiesWrapper.style.height = 'auto';
					amenitiesWrapper.style.maxHeight = 'auto';
				} else {
					const innerHeight = innerDiv.offsetHeight;
					amenitiesWrapper.style.height = `${innerHeight}px`;
					amenitiesWrapper.style.maxHeight = `${innerHeight}px`;
				}
			}
		};

		// Use ResizeObserver for better performance and more accurate detection
		const resizeObserver = new ResizeObserver(() => {
			// Add a small delay to ensure layout is complete
			setTimeout(setAmenitiesHeight, 10);
		});

		// Observe the inner div for size changes
		const innerDiv = document.querySelector('.new-div-inner');
		if (innerDiv) {
			resizeObserver.observe(innerDiv);
		}

		// Initial height setting
		setAmenitiesHeight();

		// Fallback: still listen to window resize
		const handleResize = () => {
			setTimeout(setAmenitiesHeight, 10);
		};

		window.addEventListener('resize', handleResize);

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('resize', handleResize);
		};
	}, [isPreloading]);

	useEffect(() => {
		if (images.length > 0) {
			// Preload all images in background without blocking UI
			preloadImages(0, images.length).catch(console.warn);
		}
	}, [images]);

	// Preload adjacent images when navigating in lightbox
	useEffect(() => {
		if (lightboxOpen && images.length > 1) {
			// Preload next and previous images
			preloadImages(photoIndex, 3).catch(console.warn);
		}
	}, [lightboxOpen, photoIndex, images]);

	// Early return AFTER all hooks
	if (!room) return <div>Room not found.</div>;

	return (
		<div className="main-body">
			<div className="page-banner-wrapper">
				<h2 className="page-banner-title">Rooms</h2>
				<img
					className="page-banner"
					src="https://thehighpoint.s3.ca-central-1.amazonaws.com/banner-room.jpg"
					alt="Rooms"
				/>
			</div>

			<div className="detail-breadcrumbs">
				<Link to="/rooms" className="link-block-3 w-inline-block">
					<div className="paragraph">Rooms / </div>
				</Link>
				<div className="detail-breadcrumbs-room">
					<div className="room-name">{room.eng_name}</div>
				</div>
			</div>

			<div id="about-room" className="about-room">
				<div className="room-detail">
					<div className="new-div-outer">
						<div className="new-div-inner">
							<div
								className="w-inline-block w-lightbox"
								style={{
									cursor: isPreloading ? 'wait' : 'pointer',
									opacity: isPreloading ? 0.7 : 1,
									transition: 'opacity 0.3s ease',
								}}
								onClick={() =>
									!isPreloading && handleImageClick(0)
								}
							>
								<img
									className="detail-main-image"
									src={room.img}
									alt={room.eng_name}
								/>
								{isPreloading && (
									<div
										style={{
											position: 'absolute',
											top: '50%',
											left: '50%',
											transform: 'translate(-50%, -50%)',
											background: 'rgba(0, 0, 0, 0.7)',
											color: 'white',
											padding: '8px 16px',
											borderRadius: '4px',
											fontSize: '14px',
										}}
									>
										Loading...
									</div>
								)}
							</div>
							<ImageSlider
								images={room.images}
								onImageClick={(index) =>
									!isPreloading && handleImageClick(index + 1)
								}
							/>
						</div>
						<div className="amenities-tiles-wrapper">
							<Typography
								id="our-amenities"
								align="center"
								variant="h4"
								sx={{ width: '100%', marginBottom: '20px' }}
							>
								Our Amenities
							</Typography>
							<div className="amenities-tiles">
								{room.amenities.map((amenity, index) => (
									<div className="amenity-tile" key={index}>
										<img
											src={amenityIcons[amenity]}
											alt={amenity}
											loading="lazy"
										></img>
									</div>
								))}
							</div>
						</div>
					</div>
					<div className="column">
						<div className="row">
							<div className="subheading-two-left">
								{room.eng_name}
							</div>
							<div className="heading-two-left">
								About This Room
							</div>
						</div>
						<a
							href="https://live.ipms247.com/booking/book-rooms-426634"
							className="book-now-in-detail"
							target="_blank"
							rel="noopener noreferrer"
						>
							BOOK NOW
						</a>
					</div>
					<div className="paragraph">
						<b>{room.bold_context}</b>
						{room.contexts.map((context, index) => (
							<p>{context}</p>
						))}
					</div>
				</div>
			</div>
			{lightboxOpen && (
				<Lightbox
					mainSrc={images[photoIndex]}
					nextSrc={images[(photoIndex + 1) % images.length]}
					prevSrc={
						images[(photoIndex + images.length - 1) % images.length]
					}
					onCloseRequest={() => setLightboxOpen(false)}
					onMovePrevRequest={() =>
						setPhotoIndex(
							(photoIndex + images.length - 1) % images.length
						)
					}
					onMoveNextRequest={() =>
						setPhotoIndex((photoIndex + 1) % images.length)
					}
				/>
			)}
		</div>
	);
}
